// src/pages/ForgotPassword.js
import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../services/api';
import logo from '../assets/logo.png';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: reset
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [timerId, setTimerId] = useState(null);
  const navigate = useNavigate();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));
  const resetIdentifier = useMemo(() => {
    const raw = String(identifier || '').trim();
    const email = raw.toLowerCase();
    const mobile = raw.replace(/\D/g, '');
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isMobile = /^\d{10,15}$/.test(mobile);

    return {
      isEmail,
      isMobile,
      payload: isEmail
        ? { identifier: email, email }
        : { identifier: mobile, mobile },
    };
  }, [identifier]);

  useEffect(() => () => {
    if (timerId) clearInterval(timerId);
  }, [timerId]);

  const startCooldown = (sec = 60) => {
    setCooldown(sec);
    if (timerId) clearInterval(timerId);
    const id = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTimerId(id);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setMsg('');
    if (!resetIdentifier.isEmail && !resetIdentifier.isMobile) {
      setError('Please enter a valid registered mobile number or email');
      return;
    }
    setLoading(true);
    try {
      const res = await API.post('/auth/forgot-password', resetIdentifier.payload);
      setMsg(res?.data?.message || 'OTP sent successfully');
      setStep(2);
      startCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(''); setMsg('');
    if (!otp || otp.length < 4) {
      setError('Enter the OTP');
      return;
    }
    setLoading(true);
    try {
      await API.post('/auth/verify-otp', { ...resetIdentifier.payload, otp });
      setMsg('OTP verified. You can set a new password.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setMsg('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { ...resetIdentifier.payload, otp, newPassword });
      setMsg('Password reset successfully. You can login now.');
      // Optionally redirect to login after short delay
      // setTimeout(() => (window.location.href = '/login'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(''); setMsg('');
    try {
      const res = await API.post('/auth/forgot-password', resetIdentifier.payload);
      setMsg(res?.data?.message || 'OTP resent.');
      startCooldown(45);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not resend OTP');
    }
  };

  return (
    <div className="fp-shell">
      <header className="fp-topbar">
        <div className="topbar-left">
          <button className="topbar-back" type="button" onClick={goBack} aria-label="Go back">
            <span className="material-symbols-outlined">arrow_back_ios_new</span>
          </button>
          <div className="brand">
            <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." className="brand-logo" />
            <span>Lotus Rice Mill Pvt. Ltd.</span>
          </div>
        </div>
        <button className="topbar-btn" type="button" onClick={() => navigate('/')}>Login</button>
      </header>

      <main className="fp-main">
        <Container className="d-flex justify-content-center align-items-center px-3">
          <Card className="fp-card p-4 shadow">
            <motion.h4
              className="mb-1 fw-semibold text-center"
              initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            >
              Forgot Password
            </motion.h4>
            <p className="text-muted text-center mb-4" style={{ fontSize: '0.9rem' }}>
              {step === 1 && 'Enter your registered mobile number or email'}
              {step === 2 && 'Enter the OTP sent to your registered contact'}
              {step === 3 && 'Set a new password'}
            </p>

            {msg && <Alert variant="success">{msg}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}

            {/* STEP 1: Mobile or Email */}
            {step === 1 && (
              <Form onSubmit={handleSendOtp}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number or Email</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter registered mobile or email"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                  />
                </Form.Group>
                <Button type="submit" className="w-100" variant="success" disabled={loading}>
                  {loading ? 'Sending...' : 'Send OTP'}
                </Button>
              </Form>
            )}

            {/* STEP 2: OTP */}
            {step === 2 && (
              <Form onSubmit={handleVerifyOtp}>
                <Form.Group className="mb-3">
                  <Form.Label>OTP</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                  />
                </Form.Group>
                <Row className="mb-3">
                  <Col className="text-start">
                    <Button variant="link" className="p-0" onClick={() => setStep(1)}>Change mobile/email</Button>
                  </Col>
                  <Col className="text-end">
                    <Button variant="link" className="p-0" onClick={handleResend} disabled={cooldown > 0}>
                      Resend OTP {cooldown > 0 ? `(${cooldown}s)` : ''}
                    </Button>
                  </Col>
                </Row>
                <Button type="submit" className="w-100" variant="success" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </Button>
              </Form>
            )}

            {/* STEP 3: Reset */}
            {step === 3 && (
              <Form onSubmit={handleResetPassword}>
                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Re-enter new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </Form.Group>
                <Button type="submit" className="w-100" variant="success" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Form>
            )}
          </Card>
        </Container>
      </main>
    </div>
  );
};

export default ForgotPassword;
